import { DateWindow, IsoDate, datesIn, lengthInDays } from './windows'

export type DailyRow = {
    domainId: number
    date: IsoDate
    impressions: number
    clicks: number
    sends: number
    purchases: number
    purchaseAmount: number
    permissionsShown: number
    subscriptions: number
    /** The domain's subscriber count sampled that day, or null when no sample exists. A level, never summed across days. */
    subscribers: number | null
}

/** The measures that are flows: they add up across days. Subscribers is a level and has its own accessors. */
export type DailyMeasure = Exclude<keyof DailyRow, 'domainId' | 'date' | 'subscribers'>

const isInScope = (row: DailyRow, window: DateWindow, domainIds?: number[]): boolean =>
    row.date >= window.since && row.date <= window.through && (!domainIds || domainIds.includes(row.domainId))

type SampledRow = DailyRow & { subscribers: number }

/** The rows that carry a subscriber sample on the date, for the given domains (every domain when none are given). */
const sampledRowsOn = (rows: DailyRow[], date: IsoDate, domainIds?: number[]): SampledRow[] =>
    rows.filter(
        (row): row is SampledRow =>
            row.date === date && row.subscribers !== null && (!domainIds || domainIds.includes(row.domainId)),
    )

export const levelOn = (rows: DailyRow[], date: IsoDate, domainIds?: number[]): number | null => {
    const sampled = sampledRowsOn(rows, date, domainIds)
    return sampled.length ? sampled.reduce((total, row) => total + row.subscribers, 0) : null
}

export const dailyLevels = (rows: DailyRow[], window: DateWindow, domainIds?: number[]): Array<number | null> =>
    datesIn(window).map((date) => levelOn(rows, date, domainIds))

const sampledLevelsByDomainOn = (rows: DailyRow[], date: IsoDate, domainIds?: number[]): Map<number, number> =>
    new Map(sampledRowsOn(rows, date, domainIds).map((row) => [row.domainId, row.subscribers]))

export type LevelPair = { prior: number; current: number }

/**
 * The level on two dates, taken over the domains sampled on both. A domain whose snapshot is late on one of the
 * dates would otherwise look like a loss (or a gain) the size of its whole audience. Null when no domain is
 * sampled on both dates: there is nothing to compare, which is not the same as no change.
 */
export const levelPairOn = (
    rows: DailyRow[],
    priorDate: IsoDate,
    currentDate: IsoDate,
    domainIds?: number[],
): LevelPair | null => {
    const priorLevels = sampledLevelsByDomainOn(rows, priorDate, domainIds)
    const currentLevels = sampledLevelsByDomainOn(rows, currentDate, domainIds)
    let prior = 0
    let current = 0
    let sharedDomains = 0
    for (const [domainId, priorLevel] of priorLevels) {
        const currentLevel = currentLevels.get(domainId)
        if (currentLevel === undefined) continue
        prior += priorLevel
        current += currentLevel
        sharedDomains += 1
    }
    return sharedDomains ? { prior, current } : null
}

/**
 * The level's change per day between each pair of consecutive dates that have a sample, compared like for like
 * (see `levelPairOn`) and divided by the days between the two dates, so a gap in sampling is not read as a
 * one-day step. A pair that shares no sampled domain contributes nothing.
 */
export const dailyLevelChanges = (rows: DailyRow[], window: DateWindow, domainIds?: number[]): number[] => {
    const sampledDates = datesIn(window).filter((date) => levelOn(rows, date, domainIds) !== null)
    const changes: number[] = []
    sampledDates.slice(1).forEach((date, index) => {
        const previousDate = sampledDates[index]
        const pair = levelPairOn(rows, previousDate, date, domainIds)
        if (pair) changes.push((pair.current - pair.prior) / (lengthInDays({ since: previousDate, through: date }) - 1))
    })
    return changes
}

export const sumMeasure = (rows: DailyRow[], measure: DailyMeasure, window: DateWindow, domainIds?: number[]): number =>
    rows.reduce((total, row) => (isInScope(row, window, domainIds) ? total + (row[measure] || 0) : total), 0)

export const dailySeries = (
    rows: DailyRow[],
    measure: DailyMeasure,
    window: DateWindow,
    domainIds?: number[],
): number[] => {
    const totalsByDate = new Map<IsoDate, number>(datesIn(window).map((date) => [date, 0]))
    for (const row of rows) {
        if (isInScope(row, window, domainIds))
            totalsByDate.set(row.date, (totalsByDate.get(row.date) ?? 0) + (row[measure] || 0))
    }
    return [...totalsByDate.values()]
}

export const dailyRates = (
    rows: DailyRow[],
    numerator: DailyMeasure,
    denominator: DailyMeasure,
    window: DateWindow,
    domainIds?: number[],
): Array<number | null> => {
    const numerators = dailySeries(rows, numerator, window, domainIds)
    const denominators = dailySeries(rows, denominator, window, domainIds)
    return numerators.map((value, index) => (denominators[index] === 0 ? null : value / denominators[index]))
}

/**
 * A chart of a rate draws a day with nothing to divide by as zero, like a count that was zero, so the line spans
 * the whole range: a line broken into isolated points between gaps draws little or nothing and reads as a missing
 * chart. Only charts use this; the statistics that compare a rate with its baseline keep reading those days as
 * absent, so they are fed `dailyRates` directly.
 */
export const zeroWhereNoDayHasAnythingToDivideBy = (series: Array<number | null>): number[] =>
    series.map((value) => value ?? 0)
